import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/Buttons/Button";
import DataTable from "../../../components/DataTable/DataTable";
import { IColInfo } from "../../../components/DataTable/Interfaces";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemRevisionModel from "../../../models/ItemRevisionModel";
import WorkflowDefinitionModel from "../../../models/WorkflowDefinitionModel";
import WorkflowModel from "../../../models/WorkflowModel";
import WorkflowTaskModel from "../../../models/WorkflowTaskModel";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { DataResult, Result } from "../../../types/Result";
import FormatUtils from "../../../utils/FormatUtils";

interface IProps extends PopupProps {
    itemRevision: ItemRevisionModel
}

interface StartWorkflowModel {
    itemRevisionId: number
    workflowDefinitionId: number
    workflowExecutionTypeId?: number
    subItemRevisionIds: number[]
}

const StartWorkflowPopup: React.FC<IProps> = ({ show, onHide, itemRevision }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [subItems, setSubItems] = useState<ItemRevisionModel[]>()
    const [itemStatusTypes, setItemStatusTypes] = useState<string[]>()
    const [showSubItems, setShowSubItems] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const navigate = useNavigate();
    const formatUtils = new FormatUtils()

    const [formData, setFormData] = useState<StartWorkflowModel>({
        itemRevisionId: itemRevision.id,
        workflowDefinitionId: 0,
        workflowExecutionTypeId: 1,
        subItemRevisionIds: []
    });

    const workflowDefinitions = useSelectListOptions(`api/v1/selectlist/ItemWorkflowDefinitions?itemRevisionId=${itemRevision.id}&applicationFunctionId=${ApplicationFunctions.StartItemWorkflow}`, '-- Select workflow');
    const workflowExecutionTypes = useSelectListOptions(`api/v1/selectlist/WorkflowExecutionTypes`, '-- Select execution type');

    useEffect(() => {
        getSubItems();
    }, [itemRevision.id]);

    function submit() {
        if (formData) {
            setShowSpinner(true)

            // Select all subitems when user cannot select subitems
            if (subItems && showSubItems == false)
                formData.subItemRevisionIds = subItems.map(x => x.id)

            DataService
                .post<DataResult<WorkflowModel>>(`api/v1/Workflow/StartWorkflow`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response)
                    setShowSpinner(false)
                    if (response.isSuccesful === true) {
                        if (response.data) {
                            setShowSpinner(true)
                            DataService
                                .get<WorkflowTaskModel[]>('api/v1/WorkflowTasks/ByWorkflow/' + response.data.id)
                                .then(response => {
                                    setShowSpinner(false)
                                    //console.log('tasks', response)
                                    var activeTasks = response.filter(x => x.completedOn == null)
                                    if (activeTasks.length > 0)
                                        navigate('/plm/Workflow/Task/' + activeTasks[0].id)
                                    else if (onHide)
                                        onHide()
                                })
                                .catch(e => { handleException(e); setShowSpinner(false); });
                        }
                        else if (onHide)
                            onHide();
                    }

                })
                .catch(e => { handleException(e); setShowSpinner(false); });
        }
    }

    function getWorkflowDefinition(workflowDefinitionId: number) {
        setShowSpinner(true)
        DataService
            .get<WorkflowDefinitionModel>('api/v1/WorkflowDefinitions/' + workflowDefinitionId)
            .then(response => {
                setShowSpinner(false)
                if (response.workflowExecutionTypeId)
                    formData.workflowExecutionTypeId = response.workflowExecutionTypeId
                else
                    formData.workflowExecutionTypeId = 1

                if (!response.workflowExecutionTypeId)
                    setShowSubItems(true)
                else {
                    setShowSubItems(false)
                }
            })
            .catch(e => {
                handleException(e)
                setShowSpinner(false)
            });
    }

    function getSubItems()
    {
        if (itemRevision) {
            setShowSpinner(true)
            DataService.get<ItemRevisionModel[]>('api/v1/ItemRevisions/ByParentItemRevision/' + itemRevision.id)
                .then(response => {
                    setShowSpinner(false)

                    let statusTypes: string[] = []
                    response.forEach(x => {
                        x.itemRevisionStatuses.forEach(y => {
                            var endDateStatusType = formatUtils.dateFromString(y.itemStatus.itemStatusType.endDate)
                            var endDateStatus = formatUtils.dateFromString(y.itemStatus.endDate)
                            if ((endDateStatusType === undefined || endDateStatusType > new Date(Date.now())) &&
                                (endDateStatus === undefined || endDateStatus > new Date(Date.now()))) {
                                if (y.isLatest && statusTypes.includes(y.itemStatus.itemStatusType.name) == false) {
                                    statusTypes.push(y.itemStatus.itemStatusType.name);
                                }
                            }
                        })
                    });
                    setItemStatusTypes(statusTypes)
                    setSubItems(response)
                })
                .catch(e => { handleException(e); setShowSpinner(false); });
        }
    }

    function onSelectedItemsChanges(selectedItems: ItemRevisionModel[]) {
        if (formData)
            formData.subItemRevisionIds = selectedItems.map(x => x.id)
    }

    function onFieldChanged(name: string, value: string): void {
        if (name === 'workflowDefinitionId' && itemRevision.colors.length > 0)
            getWorkflowDefinition(Number(value))
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'workflowDefinitionId', label: 'Workflow', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'workflowExecutionTypeId', label: 'Execution type', labelSize: 3 },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    function getStatusColor(itemRevision: ItemRevisionModel, colInfo: IColInfo): string | undefined {
        if (itemRevision && itemRevision.itemStatus)
            return itemRevision.itemStatus.color;
    }

    function getStatus(itemRevision: ItemRevisionModel, colInfo: IColInfo): string | undefined {
        const status = colInfo.dataProp;
        let result = ''

        itemRevision.itemRevisionStatuses.forEach(y => {
            var endDateStatus = formatUtils.dateFromString(y.itemStatus.endDate)
            if (endDateStatus === undefined || endDateStatus > new Date(Date.now())) {
                if (y.isLatest == true && y.itemStatus.itemStatusType.name == status) {
                    result = y.itemStatus.name;
                }
            }
        })

        return result;
    }


    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { dataProp: 'col-1', getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
        { checkboxButton: { text: "Select", icon: 'fa-check', color: 'green' } },
        { title: 'Item', dataProp: '.', dataType: 'itemrevision',  sort: 'asc' },
        { title: 'Status', dataProp: 'itemStatus.name', filterInfo: { filterType: 'CheckboxList' } },
    ];

    if (itemStatusTypes) {
        itemStatusTypes.forEach((value, index) => {
            colInfos.push({ title: value, dataProp: value, getCellData: getStatus, filterInfo: { filterType: 'CheckboxList', filterMenuWidth: 325 } });
        });
    }

    return (
        <Popup show={show} onHide={onHide} size={'xl'} title={'Start workflow'} >
            <Popup.Body>
                <SelectListField {...getFieldProps('workflowDefinitionId')} options={workflowDefinitions} />
                {showSubItems && <SelectListField {...getFieldProps('workflowExecutionTypeId')} options={workflowExecutionTypes} />}
                {subItems && formData.workflowExecutionTypeId == 2 && <DataTable
                    name='dtItemStartWorkflowSubItems'
                    colInfos={colInfos}
                    tableData={subItems}
                    onSelectedRowsChange={onSelectedItemsChanges}
                />}
            </Popup.Body>
            <Popup.Footer>
                <Button {...getSubmitButtonProps()} text='Start workflow' />
            </Popup.Footer>
            {showSpinner && <SpinnerOverlay />}
        </Popup>
    );
}
export default StartWorkflowPopup;
